import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Home, Heart, MessageSquare, LogOut, MapPin, Building2, Plus, Trash2, Eye, MousePointerClick, Pencil, X, Save, ChevronRight, Upload, Camera, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import ImageCropper from '../components/ImageCropper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}/${url.replace(/^\/+/, '')}`;
};

const AMENITIES_OPTIONS = [
  'Parking', '24/7 Security', 'Gym', 'AC', 'Garden', 'Waterfront',
  'Sauna', 'Steam Room', 'Swimming Pool', 'Intercom', 'Club House', 'Meeting Room',
  'Furnished', 'Semi-Furnished', 'Equipped Kitchen', 'Solar Power', 'Heater', 'Fan',
  'Electrical Provisions', 'Vastu Compliant', 'Badminton Court', 'Basketball Court',
  'Yoga', 'EV Charging', "Children's Play Area", 'Basement Parking', 'Smart Home',
  'Amphitheater', 'Jogging Track', 'Reflexology Path', 'Drivers Room', 'Terrace',
  'Kids Pool', 'Tennis Court', 'Pickleball Court', 'Wall Climbing', 'Jacuzzi', 'Paddle Court'
];

const UserDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('my-properties');
  const [myProperties, setMyProperties] = useState([]);
  const [propertySortDir, setPropertySortDir] = useState('newest');
  const [savedProperties, setSavedProperties] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [listingClicks, setListingClicks] = useState({});
  const [editingListing, setEditingListing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [editUploading, setEditUploading] = useState(false);
  const [profilePic, setProfilePic] = useState(null);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [cropImage, setCropImage] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!storedUser || !token) {
      navigate('/login');
      return;
    }
    
    const userData = JSON.parse(storedUser);
    if (userData.role === 'admin') {
      navigate('/admin');
      return;
    }
    
    setUser(userData);
    fetchData(token);
  }, [navigate]);

  const fetchData = async (token) => {
    try {
      setLoading(true);
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [myPropsRes, savedPropsRes, enquiriesRes, metricsRes, profileRes] = await Promise.all([
        axios.get(`${API}/user/my-properties`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/user/saved-properties`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/user/enquiries`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/users/me/listing-metrics`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/user/profile`, config).catch(() => ({ data: {} }))
      ]);
      
      setMyProperties(myPropsRes.data);
      setSavedProperties(savedPropsRes.data);
      setEnquiries(enquiriesRes.data);
      if (profileRes.data.profile_pic) setProfilePic(profileRes.data.profile_pic);
      const clickMap = {};
      metricsRes.data.forEach(m => { clickMap[m.listing_id] = m.impressions; });
      setListingClicks(clickMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'This permanently deletes your PropTick account, your saved properties, the enquiries you have sent, and every property you have listed, including its photos. This cannot be undone.'
    );
    if (!confirmed) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/user/me`, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      alert('Your account has been deleted.');
      navigate('/');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete account. Please try again.');
    }
  };

  const handleUnsaveProperty = async (listingId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/user/save-property/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedProperties(savedProperties.filter(p => p.id !== listingId));
    } catch (error) {
      console.error('Error unsaving property:', error);
    }
  };

  const handleProfilePicSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropImage(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCroppedUpload = async (blob) => {
    setCropImage(null);
    setUploadingPic(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', new File([blob], 'profile.jpg', { type: 'image/jpeg' }));
      const uploadRes = await axios.post(`${API}/upload-image`, fd);
      const imageUrl = uploadRes.data.url;
      await axios.put(`${API}/user/profile-pic`, { profile_pic: imageUrl }, { headers: { Authorization: `Bearer ${token}` } });
      setProfilePic(imageUrl);
    } catch (error) {
      console.error('Error uploading profile pic:', error);
      alert('Failed to upload profile picture.');
    } finally {
      setUploadingPic(false);
    }
  };


  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/user/listings/${listingId}`, { headers: { Authorization: `Bearer ${token}` } });
      setMyProperties(prev => prev.filter(p => p.id !== listingId));
    } catch (error) {
      console.error('Error deleting listing:', error);
      alert(error.response?.data?.detail || 'Failed to delete listing');
    }
  };

  const openEditModal = (listing) => {
    setEditForm({
      title: listing.title || '',
      description: listing.description || '',
      listing_type: listing.listing_type || 'buy',
      property_type: listing.property_type || 'apartment',
      city: listing.city || '',
      locality: listing.locality || '',
      price: listing.price || '',
      size_sqft: listing.size_sqft || '',
      bedrooms: listing.bedrooms || '',
      bathrooms: listing.bathrooms || '',
      images: listing.images || [],
      amenities: listing.amenities || [],
      property_status: listing.property_status || 'ready',
      price_onwards: listing.price_onwards || false,
      rera_number: listing.rera_number || '',
      land_size: listing.land_size || '',
      connectivity: listing.connectivity || { railway_station: '', hospital: '', police_station: '', school: '', mall: '', airport: '' },
      seller_type: listing.seller_type || 'owner',
      seller_name: listing.seller_name || '',
      seller_phone: listing.seller_phone || '',
      seller_email: listing.seller_email || ''
    });
    setEditingListing(listing);
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const toggleEditAmenity = (amenity) => {
    const current = editForm.amenities || [];
    setEditForm({
      ...editForm,
      amenities: current.includes(amenity)
        ? current.filter(a => a !== amenity)
        : [...current, amenity]
    });
  };

  const handleEditImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if ((editForm.images || []).length + files.length > 20) {
      alert('Maximum 20 images allowed');
      return;
    }
    setEditUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/upload-image`, fd);
        setEditForm(prev => ({ ...prev, images: [...(prev.images || []), res.data.url] }));
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image.');
    } finally {
      setEditUploading(false);
    }
  };

  const removeEditImage = (index) => {
    setEditForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const setEditCoverImage = (index) => {
    if (index === 0) return;
    setEditForm(prev => {
      const imgs = [...(prev.images || [])];
      const [moved] = imgs.splice(index, 1);
      imgs.unshift(moved);
      return { ...prev, images: imgs };
    });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        title: editForm.title,
        description: editForm.description,
        listing_type: editForm.listing_type,
        property_type: editForm.property_type,
        city: editForm.city,
        locality: editForm.locality,
        price: parseFloat(editForm.price),
        size: editForm.size_sqft ? `${editForm.size_sqft} sq.ft` : '',
        size_sqft: editForm.size_sqft ? parseFloat(editForm.size_sqft) : null,
        bedrooms: editForm.bedrooms ? parseInt(editForm.bedrooms) : null,
        bathrooms: editForm.bathrooms ? parseInt(editForm.bathrooms) : null,
        price_onwards: editForm.price_onwards || false,
        images: editForm.images || [],
        amenities: editForm.amenities,
        property_status: editForm.property_status,
        rera_number: editForm.rera_number || null,
        land_size: editForm.land_size || null,
        connectivity: Object.values(editForm.connectivity || {}).some(v => v) ? editForm.connectivity : null,
        seller_type: editForm.seller_type,
        seller_name: editForm.seller_name,
        seller_phone: editForm.seller_phone,
        seller_email: editForm.seller_email || null
      };
      await axios.put(`${API}/user/listings/${editingListing.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingListing(null);
      fetchData(token);
    } catch (error) {
      console.error('Error updating listing:', error);
      alert('Failed to update listing. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (price) => {
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return badges[status] || 'bg-slate-100 text-slate-700';
  };

  const getPropertyStatusBadge = (ps) => {
    if (ps === 'off_plan') return 'bg-amber-100 text-amber-700';
    return 'bg-emerald-100 text-emerald-700';
  };

  const tabs = [
    { id: 'my-properties', label: 'My Properties', icon: Home, count: myProperties.length },
    { id: 'saved', label: 'Saved Properties', icon: Heart, count: savedProperties.length },
    { id: 'enquiries', label: 'Enquiries', icon: MessageSquare, count: enquiries.length }
  ];

  const SavedPropertyCard = ({ listing, onRemove }) => (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all">
      <div className="relative h-48">
        {listing.images && listing.images.length > 0 ? (
          <img src={resolveImg(listing.images[0])} alt={listing.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-slate-200 flex items-center justify-center">
            <Building2 className="w-12 h-12 text-slate-400" />
          </div>
        )}
        {listing.listing_type === 'rent' && (
          <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium bg-green-600 text-white">
            For Rent
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-lg mb-1 line-clamp-1">{listing.title}</h3>
        <div className="flex items-center gap-1 text-slate-500 text-sm mb-2">
          <MapPin className="w-4 h-4" />
          <span>{listing.locality}, {listing.city}</span>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-blue-600 font-bold text-lg">
            {formatPrice(listing.price)}
            {listing.listing_type === 'rent' && <span className="text-sm font-normal text-slate-500">/mo</span>}
          </p>
          <div className="flex items-center gap-2">
            <Link to={`/property/${listing.slug || listing.id}`} className="p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all">
              <Eye className="w-4 h-4" />
            </Link>
            <button onClick={() => onRemove(listing.id)} className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-all">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-16">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-16">
      <Navbar />
      
      <div className="px-6 md:px-12 lg:px-24 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div className="flex items-center gap-4 mb-4 md:mb-0">
              <div className="relative group">
                {profilePic ? (
                  <img src={resolveImg(profilePic)} alt="Profile" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                )}
                <label className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center cursor-pointer transition-all">
                  {uploadingPic ? (
                    <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                  <input type="file" accept="image/*" onChange={handleProfilePicSelect} className="hidden" data-testid="profile-pic-input" />
                </label>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Welcome, {user?.name || 'User'}</h1>
                <p className="text-slate-500">{user?.email}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Link
                to="/list-property"
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all"
                data-testid="list-property-btn"
              >
                <Plus className="w-5 h-5" />
                List Property
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-all"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl border border-slate-200 mb-6">
            <div className="flex border-b border-slate-200">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-4 px-4 font-medium transition-all ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="min-h-[400px]">
            {/* My Properties Tab - List View */}
            {activeTab === 'my-properties' && (
              <div>
                {myProperties.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                    <Home className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-700 mb-2">No properties listed yet</h3>
                    <p className="text-slate-500 mb-6">Start by listing your first property</p>
                    <Link
                      to="/list-property"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all"
                    >
                      <Plus className="w-5 h-5" />
                      List Your Property
                    </Link>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="my-properties-list">
                    {/* Sort Controls */}
                    <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
                      <span className="text-sm text-slate-500">{myProperties.length} Properties</span>
                      <select
                        value={propertySortDir}
                        onChange={(e) => setPropertySortDir(e.target.value)}
                        className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white"
                        data-testid="property-sort"
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                      </select>
                    </div>
                    {/* List Header */}
                    <div className="hidden md:grid md:grid-cols-12 gap-4 px-5 py-3 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <div className="col-span-4">Property</div>
                      <div className="col-span-1">Price</div>
                      <div className="col-span-1 text-center">Type</div>
                      <div className="col-span-1 text-center">Status</div>
                      <div className="col-span-1 text-center">Views</div>
                      <div className="col-span-1 text-center">Date Listed</div>
                      <div className="col-span-3 text-right">Actions</div>
                    </div>
                    {/* List Items */}
                    {[...myProperties].sort((a, b) => {
                      const da = new Date(a.created_at || 0).getTime();
                      const db_t = new Date(b.created_at || 0).getTime();
                      return propertySortDir === 'newest' ? db_t - da : da - db_t;
                    }).map((listing, idx) => (
                      <div
                        key={listing.id}
                        className={`grid grid-cols-1 md:grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-slate-50 transition-colors ${
                          idx !== myProperties.length - 1 ? 'border-b border-slate-100' : ''
                        }`}
                        data-testid={`my-property-row-${listing.id}`}
                      >
                        {/* Property Info */}
                        <div className="col-span-4 flex items-center gap-3">
                          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-slate-200">
                            {listing.images && listing.images.length > 0 ? (
                              <img src={resolveImg(listing.images[0])} alt={listing.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Building2 className="w-6 h-6 text-slate-400" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-semibold text-slate-900 truncate">{listing.title}</h4>
                            <div className="flex items-center gap-1 text-slate-500 text-sm">
                              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="truncate">{listing.locality}, {listing.city}</span>
                            </div>
                            {listing.property_status && (
                              <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${getPropertyStatusBadge(listing.property_status)}`}>
                                {listing.property_status === 'off_plan' ? 'Off Plan' : 'Ready'}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Price */}
                        <div className="col-span-1">
                          <p className="font-bold text-blue-600">
                            {formatPrice(listing.price)}
                            {listing.listing_type === 'rent' && <span className="text-xs font-normal text-slate-500">{listing.rent_period === 'per_day' ? '/day' : '/mo'}</span>}
                          </p>
                        </div>
                        {/* Type */}
                        <div className="col-span-1 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            listing.listing_type === 'rent' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {listing.listing_type === 'rent' ? 'Rent' : 'Buy'}
                          </span>
                        </div>
                        {/* Status */}
                        <div className="col-span-1 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(listing.status)}`}>
                            {listing.status}
                          </span>
                        </div>
                        {/* Views */}
                        <div className="col-span-1 text-center">
                          <span className="inline-flex items-center gap-1 text-sm text-slate-600" data-testid={`listing-clicks-${listing.id}`}>
                            <MousePointerClick className="w-3.5 h-3.5 text-blue-500" />
                            {listingClicks[listing.id] || 0}
                          </span>
                        </div>
                        {/* Date Listed */}
                        <div className="col-span-1 text-center">
                          <span className="text-xs text-slate-500" data-testid={`listing-date-${listing.id}`}>
                            {listing.created_at ? new Date(listing.created_at).toLocaleDateString() : '—'}
                          </span>
                        </div>
                        {/* Actions */}
                        <div className="col-span-3 flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(listing)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all"
                            data-testid={`edit-listing-${listing.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            Edit
                          </button>
                          <Link
                            to={`/property/${listing.slug || listing.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all"
                            data-testid={`view-listing-${listing.id}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View
                          </Link>
                          <button
                            onClick={() => handleDeleteListing(listing.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-all"
                            data-testid={`delete-listing-${listing.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Saved Properties Tab */}
            {activeTab === 'saved' && (
              <div>
                {savedProperties.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                    <Heart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-700 mb-2">No saved properties</h3>
                    <p className="text-slate-500 mb-6">Save properties you like to view them later</p>
                    <Link
                      to="/properties"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all"
                    >
                      Browse Properties
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {savedProperties.map((listing) => (
                      <SavedPropertyCard 
                        key={listing.id} 
                        listing={listing} 
                        onRemove={handleUnsaveProperty}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Enquiries Tab */}
            {activeTab === 'enquiries' && (
              <div>
                {enquiries.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                    <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-700 mb-2">No enquiries yet</h3>
                    <p className="text-slate-500 mb-6">Your property enquiries will appear here</p>
                    <Link
                      to="/properties"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all"
                    >
                      Browse Properties
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {enquiries.map((enquiry) => (
                      <div key={enquiry.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
                        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                          {enquiry.listing?.images?.[0] ? (
                            <img src={resolveImg(enquiry.listing.images[0])} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                              <Building2 className="w-8 h-8 text-slate-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold">{enquiry.listing?.title || 'Property'}</h4>
                          <p className="text-sm text-slate-500">
                            {enquiry.listing?.locality}, {enquiry.listing?.city}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              enquiry.contact_type === 'whatsapp' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {enquiry.contact_type === 'whatsapp' ? 'WhatsApp' : 'Call'}
                            </span>
                            <span className="text-xs text-slate-400">
                              {new Date(enquiry.timestamp).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-blue-600 font-bold">
                            {enquiry.listing?.price && formatPrice(enquiry.listing.price)}
                          </p>
                          <Link
                            to={`/property/${enquiry.listing_id}`}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            View Property
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Account */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8 text-center">
        <button
          onClick={handleDeleteAccount}
          className="text-sm text-slate-400 underline hover:text-red-500 transition-colors"
          data-testid="delete-account-link"
        >
          Delete account
        </button>
      </div>

      {/* Edit Listing Modal */}
      {editingListing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="edit-listing-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h2 className="text-xl font-bold text-slate-900">Edit Listing</h2>
              <button onClick={() => setEditingListing(null)} className="p-2 rounded-lg hover:bg-slate-100 transition-all" data-testid="close-edit-modal">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input type="text" name="title" value={editForm.title} onChange={handleEditChange}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500" data-testid="edit-title" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea name="description" value={editForm.description} onChange={handleEditChange} rows={3}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-blue-100 focus:border-blue-500" data-testid="edit-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Listing Type</label>
                  <select name="listing_type" value={editForm.listing_type} onChange={handleEditChange}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3" data-testid="edit-listing-type">
                    <option value="buy">Buy</option>
                    <option value="rent">Rent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Property Type</label>
                  <select name="property_type" value={editForm.property_type} onChange={handleEditChange}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3" data-testid="edit-property-type">
                    <option value="apartment">Apartment</option>
                    <option value="villa">Villa</option>
                    <option value="land">Land</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
                  <select name="city" value={editForm.city} onChange={handleEditChange}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3" data-testid="edit-city">
                    <option value="Trivandrum">Trivandrum</option>
                    <option value="Kochi">Kochi</option>
                    <option value="Calicut">Calicut</option>
                    <option value="Palakkad">Palakkad</option>
                    <option value="Thrissur">Thrissur</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Locality</label>
                  <input type="text" name="locality" value={editForm.locality} onChange={handleEditChange}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3" data-testid="edit-locality" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Price (INR)</label>
                  <input type="number" name="price" value={editForm.price} onChange={handleEditChange}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3" data-testid="edit-price" />
                  {editForm.property_status === 'off_plan' && (
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input type="checkbox" checked={editForm.price_onwards || false} onChange={e => setEditForm({...editForm, price_onwards: e.target.checked})} className="w-4 h-4 rounded border-slate-300 accent-blue-600" data-testid="edit-price-onwards" />
                      <span className="text-xs text-slate-600">Onwards</span>
                    </label>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Sq. Ft.</label>
                  <input type="number" name="size_sqft" value={editForm.size_sqft} onChange={handleEditChange}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3" data-testid="edit-size-sqft" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bedrooms</label>
                  <select name="bedrooms" value={editForm.bedrooms} onChange={handleEditChange}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3" data-testid="edit-bedrooms">
                    <option value="">-</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bathrooms</label>
                  <select name="bathrooms" value={editForm.bathrooms} onChange={handleEditChange}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3" data-testid="edit-bathrooms">
                    <option value="">-</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5+</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Property Status</label>
                <div className="flex gap-3" data-testid="edit-property-status">
                  <button type="button" onClick={() => setEditForm({ ...editForm, property_status: 'ready' })}
                    className={`flex-1 h-10 rounded-lg font-medium border-2 transition-all text-sm ${
                      editForm.property_status === 'ready' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`} data-testid="edit-status-ready">Ready</button>
                  <button type="button" onClick={() => setEditForm({ ...editForm, property_status: 'off_plan' })}
                    className={`flex-1 h-10 rounded-lg font-medium border-2 transition-all text-sm ${
                      editForm.property_status === 'off_plan' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`} data-testid="edit-status-off-plan">Off Plan</button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">RERA Number <span className="text-slate-400 font-normal">(Optional)</span></label>
                <input type="text" name="rera_number" value={editForm.rera_number || ''} onChange={handleEditChange}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3" placeholder="e.g., K-RERA/PRJ/TVM/087/2025" data-testid="edit-rera-number" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Seller Type</label>
                <select value={editForm.seller_type || 'owner'} onChange={e => setEditForm({...editForm, seller_type: e.target.value})}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3" data-testid="edit-seller-type">
                  <option value="owner">Owner</option>
                  <option value="agent">Agent</option>
                  <option value="builder">Builder</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Land Size <span className="text-slate-400 font-normal">(Optional)</span></label>
                <input type="text" name="land_size" value={editForm.land_size || ''} onChange={handleEditChange}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3" placeholder="e.g., 5 cents" data-testid="edit-land-size" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Connectivity</label>
                <div className="space-y-4">
                  {[
                    { key: 'railway_station', label: 'Railway Station' },
                    { key: 'hospital', label: 'Hospital' },
                    { key: 'police_station', label: 'Police Station' },
                    { key: 'school', label: 'School' },
                    { key: 'mall', label: 'Mall' },
                    { key: 'airport', label: 'Airport' }
                  ].map(({ key, label }) => {
                    const valMap = { 'Under 5 km': 1, 'Under 10 km': 2, 'Under 15 km': 3, 'Above 15 km': 4 };
                    const reverseMap = { 0: '', 1: 'Under 5 km', 2: 'Under 10 km', 3: 'Under 15 km', 4: 'Above 15 km' };
                    const current = valMap[(editForm.connectivity || {})[key]] || 0;
                    return (
                      <div key={key}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium text-slate-600">{label}</span>
                          <span className="text-[11px] font-semibold text-blue-600">{(editForm.connectivity || {})[key] || 'Not set'}</span>
                        </div>
                        <input type="range" min="0" max="4" step="1" value={current}
                          onChange={(e) => setEditForm({ ...editForm, connectivity: { ...(editForm.connectivity || {}), [key]: reverseMap[Number(e.target.value)] } })}
                          className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-blue-600" />
                        <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                          <span>-</span><span>5</span><span>10</span><span>15</span><span>15+</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">Images ({(editForm.images || []).length}/20) — Click to set cover</label>
                  {(editForm.images || []).length < 20 && (
                    <label htmlFor="user-edit-image-upload" className={`h-8 px-3 rounded-lg text-xs font-medium flex items-center gap-1.5 cursor-pointer transition-all ${
                      editUploading ? 'bg-slate-100 text-slate-400' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`} data-testid="user-edit-add-images">
                      {editUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {editUploading ? 'Uploading...' : 'Add Images'}
                      <input id="user-edit-image-upload" type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple
                        onChange={handleEditImageUpload} className="hidden" disabled={editUploading} />
                    </label>
                  )}
                </div>
                {(editForm.images || []).length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    {(editForm.images || []).map((img, i) => (
                      <div key={i} className={`relative group rounded-lg overflow-hidden h-20 bg-slate-100 border-2 ${i === 0 ? 'border-blue-500' : 'border-transparent'}`}>
                        <img src={resolveImg(img)} alt={`Image ${i + 1}`} className="w-full h-full object-cover" />
                        {i === 0 && (
                          <span className="absolute top-1 left-1 text-[10px] font-bold bg-blue-600 text-white px-1.5 py-0.5 rounded" data-testid="edit-cover-badge">Cover</span>
                        )}
                        {i !== 0 && (
                          <button type="button" onClick={() => setEditCoverImage(i)}
                            className="absolute bottom-1 left-1 text-[10px] font-semibold bg-white/90 text-slate-700 px-1.5 py-0.5 rounded sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-blue-600 hover:text-white"
                            data-testid={`edit-set-cover-${i}`}>
                            Set as Cover
                          </button>
                        )}
                        <button type="button" onClick={() => removeEditImage(i)}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Amenities</label>
                <div className="flex flex-wrap gap-2" data-testid="edit-amenities">
                  {AMENITIES_OPTIONS.map((amenity) => (
                    <button key={amenity} type="button" onClick={() => toggleEditAmenity(amenity)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                        (editForm.amenities || []).includes(amenity)
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}>
                      {amenity}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Seller Name</label>
                  <input type="text" name="seller_name" value={editForm.seller_name} onChange={handleEditChange}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3" data-testid="edit-seller-name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Seller Phone</label>
                  <input type="tel" name="seller_phone" value={editForm.seller_phone} onChange={handleEditChange}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3" data-testid="edit-seller-phone" />
                </div>
              </div>
              <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                Editing your listing will set its status back to "Pending" for admin re-approval.
              </p>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setEditingListing(null)}
                className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-all" data-testid="cancel-edit-btn">
                Cancel
              </button>
              <button onClick={handleSaveEdit} disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all disabled:opacity-50" data-testid="save-edit-btn">
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Cropper Modal */}
      {cropImage && (
        <ImageCropper
          imageSrc={cropImage}
          onCropDone={handleCroppedUpload}
          onCancel={() => setCropImage(null)}
          aspectRatio={1}
          cropShape="round"
        />
      )}
    </div>
  );
};

export default UserDashboard;
